import { TripLeg, TripStop } from '../types/trip';
import { middleStopNights } from './tripRoles';

const EARTH_RADIUS_KM = 6371;
const DEFAULT_SPEED_KMH = 70;

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

export function haversineDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

export function estimateDriveMinutes(distanceKm?: number): number | undefined {
  if (distanceKm === undefined || Number.isNaN(distanceKm)) return undefined;
  return Math.round((distanceKm / DEFAULT_SPEED_KMH) * 60);
}

/**
 * Straight-line km understate the drive: roads wind. Across a benchmark of
 * Australian legs (Melbourne → Lakes Entrance 265 km straight vs 318 km road,
 * Melbourne → Adelaide 654 vs 727, Sydney → Eden 340 vs 455) the road runs
 * 1.1–1.35× the crow-flies figure, so estimates use 1.2× at 85 km/h. They're
 * still labelled "approx." — this only makes the guess a better one.
 */
export const ROAD_DETOUR_FACTOR = 1.2;
export const ESTIMATE_SPEED_KMH = 85;

export function buildTripLegs(stops: TripStop[]): TripLeg[] {
  const legs: TripLeg[] = [];
  for (let i = 1; i < stops.length; i += 1) {
    const prev = stops[i - 1];
    const current = stops[i];
    let distanceKm: number | undefined;
    if (
      typeof prev.lat === 'number' &&
      typeof prev.lng === 'number' &&
      typeof current.lat === 'number' &&
      typeof current.lng === 'number'
    ) {
      distanceKm = Number(
        (haversineDistanceKm(prev.lat, prev.lng, current.lat, current.lng) * ROAD_DETOUR_FACTOR).toFixed(1)
      );
    }
    const driveMinutes =
      typeof distanceKm === 'number' ? Math.round((distanceKm / ESTIMATE_SPEED_KMH) * 60) : undefined;
    legs.push({
      fromId: prev.id,
      toId: current.id,
      distanceKm,
      driveMinutes,
      source: 'estimate',
    });
  }
  return legs;
}

/** Default time the car leaves each morning: 9:00 am, as minutes after midnight. */
export const DEFAULT_DAY_START_MINUTES = 9 * 60;

export interface TripDay {
  /** 0-based position in the list of DRIVING days. */
  index: number;
  /** Indexes into the stops array: the day's origin … where it ends. */
  firstStopIndex: number;
  lastStopIndex: number;
  distanceKm?: number;
  driveMinutes?: number;
  /** Calendar days after departure (Day 1 = 0). Jumps past rest days, so a
   *  2-night stop makes the next driving day "Day N+2". */
  dayOffset: number;
  /** Whole days spent at the origin before this day's drive (nights − 1). */
  restDaysBefore: number;
  /** Minutes after midnight the day's drive starts. */
  startMinutes: number;
  /** Minutes after midnight you reach the day's last stop (may pass 1440). */
  arriveMinutes?: number;
  /** True when any leg that day is an estimate rather than a routed road figure. */
  estimated: boolean;
}

export interface SegmentOptions {
  /** When each day's drive starts, minutes after midnight (default 9:00 am). */
  dayStartMinutes?: number;
}

/** Nights a stop holds at a given position (start and end hold none). */
function nightsAt(stops: TripStop[], index: number): number {
  if (index <= 0 || index >= stops.length - 1) return 0;
  return middleStopNights(stops[index]);
}

/**
 * Split an ordered stop list into driving days.
 *
 * A day ends wherever you sleep: every middle stop with ≥1 night closes the
 * day it's reached on. Inside a run of via stops the daily drive budget still
 * splits the drive (the next day starts at the last stop that fits), so a
 * 12-hour run with no overnight set still reads as two days. Legs without a
 * drive time count as 0 (they can't split a day).
 *
 * Each day carries its calendar offset (rest days at multi-night stops push
 * later days along), its start time and the time you arrive, so headers can
 * say "Day 2 · Sun 19 Oct · arrive 12:40 pm". Works fully client-side, so
 * guests and Google-fallback trips get the same days.
 */
export function segmentIntoDays(
  stops: TripStop[],
  legs: TripLeg[],
  maxDriveMinutesPerDay: number = 360,
  options: SegmentOptions = {}
): TripDay[] {
  if (stops.length < 2) return [];
  const startMinutes = options.dayStartMinutes ?? DEFAULT_DAY_START_MINUTES;
  const legByFrom = new Map<string, TripLeg>();
  legs.forEach((l) => legByFrom.set(l.fromId, l));

  const days: TripDay[] = [];
  let dayStart = 0;
  let dayOffset = 0;
  let restDaysBefore = 0;
  let dayMinutes = 0;
  let dayKm = 0;
  let sawMinutes = false;
  let sawKm = false;
  let missingMinutes = false;
  let estimated = false;

  const pushDay = (endIndex: number) => {
    days.push({
      index: days.length,
      firstStopIndex: dayStart,
      lastStopIndex: endIndex,
      distanceKm: sawKm ? Number(dayKm.toFixed(1)) : undefined,
      driveMinutes: sawMinutes ? Math.round(dayMinutes) : undefined,
      dayOffset,
      restDaysBefore,
      startMinutes,
      arriveMinutes: sawMinutes && !missingMinutes ? startMinutes + Math.round(dayMinutes) : undefined,
      estimated,
    });
  };

  for (let i = 1; i < stops.length; i += 1) {
    const leg = legByFrom.get(stops[i - 1].id);
    const legMinutes = typeof leg?.driveMinutes === 'number' ? leg.driveMinutes : 0;
    const legKm = typeof leg?.distanceKm === 'number' ? leg.distanceKm : 0;

    const origin = i - 1;
    const sleepsHere = origin > dayStart && nightsAt(stops, origin) >= 1;
    const overBudget = origin > dayStart && dayMinutes + legMinutes > maxDriveMinutesPerDay;
    if (sleepsHere || overBudget) {
      pushDay(origin);
      // A budget split at a via stop still means a night somewhere there.
      const nights = Math.max(1, nightsAt(stops, origin));
      dayOffset += nights;
      restDaysBefore = nights - 1;
      dayStart = origin;
      dayMinutes = 0;
      dayKm = 0;
      sawMinutes = false;
      sawKm = false;
      missingMinutes = false;
      estimated = false;
    }

    dayMinutes += legMinutes;
    dayKm += legKm;
    if (typeof leg?.driveMinutes === 'number') sawMinutes = true;
    else missingMinutes = true;
    if (typeof leg?.distanceKm === 'number') sawKm = true;
    if (!leg || leg.source === 'estimate') estimated = true;
  }
  pushDay(stops.length - 1);
  return days;
}

export interface StopTime {
  /** Minutes after midnight you reach this stop (undefined for a day's origin). */
  arriveMinutes?: number;
  /** Minutes after midnight you leave (only for a day's origin). */
  departMinutes?: number;
}

/**
 * When you reach each stop, given the days from segmentIntoDays. Each day
 * leaves at its start time; the clock runs on the drive times alone (no
 * allowance for breaks — the figure is "on the road by", not a promise).
 * Once a leg has no drive time, later stops that day get no time.
 */
export function stopTimes(stops: TripStop[], legs: TripLeg[], days: TripDay[]): StopTime[] {
  const out: StopTime[] = stops.map(() => ({}));
  const legByFrom = new Map<string, TripLeg>();
  legs.forEach((l) => legByFrom.set(l.fromId, l));
  days.forEach((day) => {
    let t: number | undefined = day.startMinutes;
    out[day.firstStopIndex] = { ...out[day.firstStopIndex], departMinutes: day.startMinutes };
    for (let j = day.firstStopIndex + 1; j <= day.lastStopIndex; j += 1) {
      const leg = legByFrom.get(stops[j - 1].id);
      t = typeof t === 'number' && typeof leg?.driveMinutes === 'number' ? t + leg.driveMinutes : undefined;
      out[j] = { ...out[j], arriveMinutes: t };
    }
  });
  return out;
}

export interface LatLngPoint {
  lat: number;
  lng: number;
}

/** Approximate km distance from point p to the segment a–b (equirectangular —
 *  plenty accurate at route-leg scales for "which leg was grabbed?"). */
export function pointToSegmentKm(p: LatLngPoint, a: LatLngPoint, b: LatLngPoint): number {
  const kmPerDegLat = 111.32;
  const kmPerDegLng = kmPerDegLat * Math.cos(toRadians(p.lat));
  const ax = a.lng * kmPerDegLng;
  const ay = a.lat * kmPerDegLat;
  const bx = b.lng * kmPerDegLng;
  const by = b.lat * kmPerDegLat;
  const px = p.lng * kmPerDegLng;
  const py = p.lat * kmPerDegLat;
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  const t = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Which leg (segment index i, between stops[i] and stops[i+1]) is closest to
 *  point p. -1 when there are fewer than 2 stops. Used to decide where a
 *  route-line drag inserts its via waypoint. */
export function nearestLegIndex(stops: LatLngPoint[], p: LatLngPoint): number {
  if (stops.length < 2) return -1;
  let best = Infinity;
  let bestIndex = 0;
  for (let i = 0; i < stops.length - 1; i += 1) {
    const d = pointToSegmentKm(p, stops[i], stops[i + 1]);
    if (d < best) {
      best = d;
      bestIndex = i;
    }
  }
  return bestIndex;
}

/**
 * Where a new stop should go so the trip still flows: the gap between two
 * existing stops that adds the least extra distance. The start and the
 * destination stay put — a new place lands somewhere in between.
 *
 * Returns the index to insert at (0..stops.length). Fewer than 2 stops →
 * append. Stops without coordinates can't be measured, so if no gap can be
 * scored it falls back to "just before the destination".
 */
export function bestInsertIndex(stops: Partial<LatLngPoint>[], p: LatLngPoint): number {
  if (stops.length < 2) return stops.length;
  let best = stops.length - 1;
  let bestCost = Infinity;
  for (let i = 1; i < stops.length; i += 1) {
    const a = stops[i - 1];
    const b = stops[i];
    if (
      typeof a.lat !== 'number' || typeof a.lng !== 'number' ||
      typeof b.lat !== 'number' || typeof b.lng !== 'number'
    ) {
      continue;
    }
    const cost =
      haversineDistanceKm(a.lat, a.lng, p.lat, p.lng) +
      haversineDistanceKm(p.lat, p.lng, b.lat, b.lng) -
      haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
    if (cost < bestCost) {
      bestCost = cost;
      best = i;
    }
  }
  return best;
}

export interface StopPlacement {
  /** Index to insert the new stop at. */
  index: number;
  /** True when the new stop becomes the destination (the old one turns into
   *  an overnight). */
  asNewEnd: boolean;
}

/**
 * Where a newly added place goes. Road trips are mostly built in order —
 * Melbourne, then Halls Gap, then Mount Gambier — so a place that lies BEYOND
 * the current destination extends the trip; only a place that's genuinely
 * between two stops is slotted in.
 *
 * Rule (straight-line km, which is plenty to rank options):
 *   detour   = the cheapest "insert between stops i-1 and i" — d(a,p) + d(p,b) − d(a,b)
 *   extend   = d(destination, p)  — the extra driving if p becomes the new end
 * Extend when extend ≤ detour, otherwise insert at the cheapest gap.
 *
 *   Melbourne → Halls Gap, add Mount Gambier: detour ≈ 330 km, extend ≈ 175 km → new end
 *   Melbourne → Adelaide, add Halls Gap:      detour ≈ 90 km,  extend ≈ 450 km → between
 *
 * Fewer than 2 stops → append. Without coordinates we can't measure, so the
 * place is appended (the build-in-order case).
 */
export function placeNewStop(stops: Partial<LatLngPoint>[], p: LatLngPoint): StopPlacement {
  if (stops.length < 2) return { index: stops.length, asNewEnd: stops.length >= 1 };
  const end = stops[stops.length - 1];
  if (typeof end.lat !== 'number' || typeof end.lng !== 'number') {
    return { index: stops.length, asNewEnd: true };
  }
  const extend = haversineDistanceKm(end.lat, end.lng, p.lat, p.lng);
  let detour = Infinity;
  let between = stops.length - 1;
  for (let i = 1; i < stops.length; i += 1) {
    const a = stops[i - 1];
    const b = stops[i];
    if (
      typeof a.lat !== 'number' || typeof a.lng !== 'number' ||
      typeof b.lat !== 'number' || typeof b.lng !== 'number'
    ) {
      continue;
    }
    const cost =
      haversineDistanceKm(a.lat, a.lng, p.lat, p.lng) +
      haversineDistanceKm(p.lat, p.lng, b.lat, b.lng) -
      haversineDistanceKm(a.lat, a.lng, b.lat, b.lng);
    if (cost < detour) {
      detour = cost;
      between = i;
    }
  }
  return extend <= detour ? { index: stops.length, asNewEnd: true } : { index: between, asNewEnd: false };
}

/** YYYY-MM-DD for a trip day, given the trip's start date (day index 0). */
export function dateForTripDay(startDate: string | null | undefined, dayIndex: number): string | null {
  if (!startDate || !/^\d{4}-\d{2}-\d{2}$/.test(startDate)) return null;
  const d = new Date(`${startDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + dayIndex);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function summariseLegs(legs: TripLeg[]): {
  totalDistanceKm?: number;
  totalDriveMinutes?: number;
} {
  const totals = legs.reduce(
    (acc, leg) => {
      if (typeof leg.distanceKm === 'number') {
        if (typeof acc.d === 'number') {
          acc.d += leg.distanceKm;
        }
      } else {
        acc.d = undefined;
      }

      if (typeof leg.driveMinutes === 'number') {
        if (typeof acc.m === 'number') {
          acc.m += leg.driveMinutes;
        }
      } else {
        acc.m = undefined;
      }

      return acc;
    },
    { d: 0 as number | undefined, m: 0 as number | undefined }
  );

  return {
    totalDistanceKm:
      typeof totals.d === 'number' ? Number(totals.d.toFixed(1)) : undefined,
    totalDriveMinutes:
      typeof totals.m === 'number' ? Math.round(totals.m) : undefined,
  };
}
